import { TextInput, Textarea, NumberInput, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { type Bedrohung } from "@iso27001/shared";
import { bedrohungenApi } from "../../api/bedrohungen.js";
import { ApiError } from "../../api/client.js";
import { useApiErrorMessage } from "../../i18n/api-error.js";
import { EntityModal } from "../../components/EntityModal.js";
import { useRecordAccess } from "../../components/use-record-access.js";

interface Props {
  opened: boolean;
  onClose: () => void;
  initial: Bedrohung | null;
}

export function BedrohungFormModal({ opened, onClose, initial }: Props) {
  const qc = useQueryClient();
  const isEdit = !!initial;
  const { t } = useTranslation();
  const apiError = useApiErrorMessage();
  // Catalogue entries have no closed state; only viewers see them read-only.
  const { readOnly } = useRecordAccess(false);
  const form = useForm({
    initialValues: {
      code: initial?.code as number | undefined,
      name: initial?.name ?? "",
      description: initial?.description ?? "",
    },
  });

  const mut = useMutation({
    mutationFn: async (vals: { code: number | undefined; name: string; description: string }) => {
      const payload: Record<string, unknown> = {
        name: vals.name,
        description: vals.description || null,
      };
      // Include code only when editing (pre-filled) or when user provided one on create
      if (isEdit || vals.code !== undefined) {
        payload.code = vals.code;
      }
      return isEdit
        ? bedrohungenApi.update(initial!.id, payload as Parameters<typeof bedrohungenApi.update>[1])
        : bedrohungenApi.create(payload as Parameters<typeof bedrohungenApi.create>[0]);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bedrohungen"] });
      notifications.show({ message: isEdit ? t("bedrohungen.form.notify.updated") : t("bedrohungen.form.notify.created"), color: "teal" });
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "Conflict") {
        form.setFieldError("code", t("bedrohungen.error.codeConflict"));
      } else {
        notifications.show({ message: apiError(e, { fallback: "bedrohungen.error.saveFailed" }), color: "red" });
      }
    },
  });

  return (
    <EntityModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t("bedrohungen.form.title") : t("bedrohungen.newThreat")}
      code={initial ? String(initial.code) : null}
      readOnly={readOnly}
      onSubmit={form.onSubmit((vals) => mut.mutate(vals))}
      saving={mut.isPending}
      submitLabel={isEdit ? t("common.action.save") : t("common.action.createSubmit")}
    >
      <Stack>
        {!readOnly && (
          <NumberInput
            label={isEdit ? t("common.field.code") : t("common.field.codeOptional")}
            description={isEdit ? undefined : t("bedrohungen.form.code.autoHint")}
            min={1}
            required={isEdit}
            placeholder={isEdit ? undefined : t("common.field.codeAutoPlaceholder")}
            {...form.getInputProps("code")}
          />
        )}
        <TextInput label={t("common.field.name")} required readOnly={readOnly} maxLength={500} {...form.getInputProps("name")} />
        <Textarea label={t("common.field.description")} readOnly={readOnly} autosize minRows={readOnly ? 1 : 2} maxLength={5000} {...form.getInputProps("description")} />
      </Stack>
    </EntityModal>
  );
}
